import React, { useEffect, useRef, useState } from "react";
import { useCallback } from "react";

import ArrowDown from "@/assets/icons/ArrowDown.svg";

export type TGlobalSelectOption = {
  id: string;
  value?: any;
  title: string;
};

type GlobalSelectProps = {
  label: string;
  options: TGlobalSelectOption[];
  selectedOption: TGlobalSelectOption | null;
  onSelect: (option: TGlobalSelectOption | null) => void;
  placeholder?: string;
  showOtherInputField?: boolean;
  position?: "absolute" | "relative";
};

function GlobalSelect({
  label,
  options,
  selectedOption,
  onSelect,
  placeholder = "Select an option...",
  showOtherInputField = true,
  position = "absolute",
}: GlobalSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isShowOtherInputField, setShowOtherInputField] = useState(false);
  const [otherInputValue, setOtherInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Prevent body scroll when dropdown is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleAddOtherOption = useCallback(() => {
    if (otherInputValue.trim() !== "") {
      const newOption: TGlobalSelectOption = {
        id: new Date().toISOString(),
        title: otherInputValue,
      };
      onSelect(newOption);
      setOtherInputValue("");
      setShowOtherInputField(false);
    }
  }, [otherInputValue, onSelect]);

  const handleShowOtherField = useCallback(() => {
    setShowOtherInputField(true);
    setTimeout(() => {
      inputRef?.current?.focus();
    }, 100);
  }, []);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const getDisplayValue = useCallback(() => {
    return selectedOption ? selectedOption.title : placeholder;
  }, [selectedOption, placeholder]);

  return (
    <div ref={dropdownRef} className="w-full relative">
      <p className="text-gray-700 font-medium text-caption-large">{label}</p>
      <div className="relative">
        <div
          className={`${
            isOpen ? "bg-[#17457D]/10 rounded-[8px]" : ""
          } p-1 mt-2`}
        >
          <div
            className={`bg-white px-[14px] transition-all duration-300 ease-in-out py-[10px] border flex justify-between ${
              isOpen ? "border-[#4066A7]" : "border-[#D0D5DD]"
            } rounded-[8px] cursor-pointer`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <div className="flex gap-x-2 items-center">
              <p className="text-[#667085] text-[15px] select-none">
                <span className="text-[#667085] font-medium">
                  {getDisplayValue()}
                </span>
              </p>
            </div>
            <div
              className={`transition-all duration-300 ease-in-out ${
                isOpen ? "rotate-180" : "rotate-0"
              }`}
            >
              {/* Replace with your arrow icon */}
              <ArrowDown />
            </div>
          </div>
        </div>

        {isOpen && (
          <div
            className={`${position} w-full z-30`}
            style={{
              position: position === "absolute" ? "absolute" : "relative",
              top: position === "absolute" ? "calc(100% + 8px)" : undefined,
            }}
          >
            <div
              ref={listRef}
              className="bg-white border border-[#F2F4F7] rounded-[8px] shadow-lg"
              style={{
                WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
                maxHeight: "48vh", // Use viewport height instead of fixed px
                overflowY: "auto",
                overscrollBehavior: "contain", // Prevent scroll chaining
              }}
            >
              <div className="p-4">
                {options.map((option) => (
                  <SelectOptionItem
                    key={option.id}
                    item={option}
                    isSelected={selectedOption?.id === option.id}
                    onSelect={onSelect}
                    setIsOpen={setIsOpen}
                  />
                ))}

                {!isShowOtherInputField && showOtherInputField ? (
                  <p
                    onClick={handleShowOtherField}
                    className="text-[15px] cursor-pointer font-medium py-[10px] text-[#101828]"
                  >
                    Others
                  </p>
                ) : (
                  showOtherInputField && (
                    <div className="relative mt-2">
                      <input
                        ref={inputRef}
                        type="text"
                        value={otherInputValue}
                        onChange={(e) => setOtherInputValue(e.target.value)}
                        placeholder="Others..."
                        className="p-2 w-full text-caption-large text-gray-500 rounded-[8px] mb-2 border outline-none border-[#4066A7]"
                      />
                      {otherInputValue && (
                        <button
                          onClick={handleAddOtherOption}
                          className="absolute text-caption2-regular bg-[#17457D] text-white px-3 py-1 font-medium rounded-[6px] top-2 right-2"
                        >
                          Add
                        </button>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

type SelectOptionItemProps = {
  item: TGlobalSelectOption;
  isSelected: boolean;
  onSelect: (option: TGlobalSelectOption | null) => void;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

function SelectOptionItem({
  item,
  isSelected,
  onSelect,
  setIsOpen,
}: SelectOptionItemProps) {
  const handleChange = () => {
    onSelect(isSelected ? null : item);
    setIsOpen(false);
  };

  return (
    <div
      onClick={handleChange}
      className="py-[10px] px-4 rounded-[6px] hover:bg-[#F9FAFB] cursor-pointer flex justify-between items-center"
    >
      <div className="inline-flex gap-x-2">
        <span className="text-[15px] font-medium text-[#101828]">
          {item.title}
        </span>
      </div>
      {isSelected && (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.3337 4L6.00033 11.3333L2.66699 8"
            stroke="#4066A7"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </div>
  );
}

export default GlobalSelect;
